import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ScreenReaderAnnouncerService } from '@app/services/screen-reader-announcer.service';
import { 
  JobListModuleComponent 
} from '../job-list-module/job-list-module.component';
import { 
  JobDescriptionModuleComponent 
} from '../job-description-module/job-description-module.component';
import { UserJobSearchService } from '@app/services/api/job-search/user-job-search.service';
import { finalize, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { setAdeptIDJobPostings, setJobList, updateJobSortByOption, updateJobUserContext, updateUserJobFilterOptions } from '@app/state/actions/jobs.action';
import { JobListSchema } from '@app/schemas/jobs';
import { Router } from '@angular/router';
import { RedirectToFindJobService } from '@app/services/signals/redirect-to-job-service';
import  jobsData from '@app/utils/jobs-data.json';
import { JobFilterFormDataSchema } from '@app/schemas/job-filter';
import { analytics } from '@app/events/segment/analytics';
import { SKILLS_GAP_MAPPING, SKILLS_MAPPING } from '@app/utils/build-your-plan';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarningSnackBarComponent } from '@app/shared/snack-bars/warning/warning.component';
import { WarningDefaultOptions } from '@app/shared/snack-bars/warning/warning.utils';
import { updateLoaderState } from '@app/state/actions/global.action';
import { setCareerGoalByOnetCode } from '@app/state/actions/careers.action';
import { JOB_FILTER_DEFAULT_OPTIONS } from '@app/utils/constants';
import { MatDialog } from '@angular/material/dialog';
import { AddResumeDialogComponent, AddResumeDialogResult } from '@app/shared/results/add-resume-dialog/add-resume-dialog.component';
import { JobLandingBannerComponent } from '../job-landing-banner/job-landing-banner.component';
import { JobLandingDialogComponent, JobLandingDialogResult } from '@app/shared/dialogs/job-landing-dialog/job-landing-dialog.component';
import { ReportedWorkExperienceService } from '@app/services/api/reported-work-experience/reported-work-experience.service';

@Component({
  selector: 'app-find-job-module',
  imports: [
    JobListModuleComponent,
    JobDescriptionModuleComponent,
    JobLandingBannerComponent,
  ],
  templateUrl: './find-job-module.component.html',
  styleUrl: './find-job-module.component.css'
})
export class FindJobModuleComponent implements OnInit {
  public selectedJobId: string = '';
  public showLoader: boolean = false;
  public loadingMessage: string = '';
  public resumeUploaded: boolean = false;
  private _snackBar = inject(MatSnackBar);
  private announcer = inject(ScreenReaderAnnouncerService);
  private dialog = inject(MatDialog);
  private reportedWorkExperienceService = inject(ReportedWorkExperienceService);
  private allJobs: JobListSchema[] = [];
  isCareerGoalSet: boolean = false;
  public userFilterOptions!: JobFilterFormDataSchema;
  private sortBy: string = '';

  @ViewChild(JobLandingBannerComponent) private jobLandingBanner?: JobLandingBannerComponent;

  constructor(
    private jobApiService: UserJobSearchService,
    private store: Store<{ jobFilter: JobFilterFormDataSchema, careerGoal: string, jobSortedBy: string }>,
    private router: Router,
    private redirectToFindJobService: RedirectToFindJobService,
  ) {
    this.store.select('jobFilter').subscribe(value => this.userFilterOptions = value);
    this.store.select('careerGoal').subscribe((value) => {
      this.isCareerGoalSet = value ? true : false;
    });
    this.store.select('jobSortedBy').subscribe((value) => {
      this.sortBy = value;
    })
  }

  ngOnInit() {
    this.redirectToFindJobService.setRedirectToFindJobStatusUpdated(false);
    /**
     * Check if user has applied filters, 
     * if yes then fetch the filtered jobs based on those options.
     */
    if(this.checkIfUserAppliedFilters()) {
      this.resumeUploaded = true;
      this.getFilteredJobs(this.userFilterOptions);
    } else {
      this.getUserJobs();
    }
  }


  getUserJobs() {
    this.showLoader = true;
    this.announcer.announce('Finding the latest job matches for you');

    this.jobApiService.getUserJobListing().pipe(
      finalize(() => {
        this.showLoader = false;
        this.announcer.announceAfterLoading('Jobs loaded.');
      })
    ).subscribe({
      next: (data) => {
        this.resumeUploaded = true;
        if(data?.userContext?.careerGoal?.onetCode) {   
          this.store.dispatch(setCareerGoalByOnetCode({ onetCode: data.userContext.careerGoal.onetCode }));
        } else {
          this.store.dispatch(updateUserJobFilterOptions({ filter: {
            ...this.userFilterOptions,
            is_related_to_career_goal: false,
          }}));
        }
        this.saveUserJobs(data);

        // track Job Match Available event
        this.trackUserJobAnalyticsEvent(data.results || []);
      },
      error: (error) => {
        if(error.status === 422) {
          this.resumeUploaded = false;
          this.store.dispatch(setJobList({ jobs: jobsData }));
        } else {
          /**
           * In case of other errors, assume resume is uploaded to avoid showing 
           * resume upload prompt.
           */
          this.resumeUploaded = true;
          this.store.dispatch(setJobList({ jobs: [] }));
          this.allJobs = [];
          this.showErrorMessage('Error while fetching job listings. Please try again!');
        }
        console.error('Error getting user jobs:', error);
      }
    });
  }

  trackUserJobAnalyticsEvent(data: any[]) {
    const jobsData = data.slice(0, 10); // Limit to first 10 jobs for analytics
    analytics.track('Job Match Available', {
      section: 'job',
      jobs_matched_count: data.length || 0,
      jobs: jobsData?.map((item: any) => {
        return {
          job_id: item.job.posting_id || null,
          job_title: item.job.job_title || null,
          match_score: item.match?.score,
          match_level: item.match?.score_category || null,
          location: `${item.job.location?.city}, ${item.job.location?.state}`,
          is_internship: item.job.is_internship ?? null,
          job_type: item.job.employment_location_type || null,
          bookmark: item.job?.is_bookmarked ?? null,
          goal_relation_checkmark: item.job.related_to_career_goal,
          employer_name: item.job.employer_name || null,
        }
      }) || [],
      match_input: ['resume'],
    });
  }

  saveUserJobs(data: any) {
    let jobs: JobListSchema[] = [];
    if (data.results && data.results.length > 0) {
      jobs = data.results.map((item: any, index: number) => {
        return {
          posting_id: item.job.posting_id,
          job_title: item.job.job_title,
          employer_name: item.job.employer_name,
          employment_type: item.job.employment_type,  
          location: this.getJobLocation(item.job.location?.city, item.job.location?.state),
          is_internship: item.job.is_internship,
          employment_location_type: item.job.employment_location_type,
          posting_date: item.job.posting_date,
          related_to_career_goal: item.job.related_to_career_goal, 
          is_bookmarked: item.job?.is_bookmarked || false,
          is_active: item.job?.is_active ?? true,
          match_score: item.match?.score || 0,
          score_category: item.match?.score_category || '',
          match_summary: item.match?.match_report?.attributes  || {},
          job_url: item.job?.url || '',
          summary: item.job.summary?.split('*').map((line: string) => line.trim()).filter((line:string) => line.length > 0) || [],
          certifications: item.job?.certifications || [],
          education: item.job?.education || [],
          salary: this.getSalaryData(item.job?.salary),
          ...this.getSkills(item.match?.match_report?.skills?.baseline || [], item.match?.match_report?.skills?.technical || []),
        };
      });
      this.store.dispatch(setAdeptIDJobPostings({ jobs: data.results }));
    }
    this.allJobs = jobs;
    this.store.dispatch(setJobList({ jobs: jobs }));
    this.store.dispatch(updateJobUserContext({ userContext: data.userContext }));
  }

  getSkills(baselineSkills: any[], techincalSkills: any []): { skills: string[], skill_gaps: string[] } {
    const highSkills: string[] = [];
    const veryHighSkills: string[] = [];
    const mediumSkills: string[] = [];
    const lowSkills: string[] = []; 

    
    const skillsSet = baselineSkills.concat(techincalSkills);
    skillsSet.map((el: any) => {
      if (el.assessment_category == SKILLS_MAPPING.VERY_HIGH) {
        veryHighSkills.push(el.skill_name);
      } else if (el.assessment_category == SKILLS_MAPPING.HIGH) {
        highSkills.push(el.skill_name);
      } else if (el.assessment_category == SKILLS_MAPPING.MEDIUM) {
        mediumSkills.push(el.skill_name);
      } else if (el.assessment_category == SKILLS_MAPPING.LOW) {
        lowSkills.push(el.skill_name);
      }
    });

     return {
      skills: veryHighSkills.length ? veryHighSkills : highSkills,
      skill_gaps: lowSkills.length ? lowSkills : mediumSkills,
    };

  }

  onClickJobItem(jobId: string) {
    this.selectedJobId = jobId;
  }

  onClickCloseBtn() {
    this.selectedJobId = '';
  }

  onClickUploadResume() {
    analytics.track('Job Upload Resume', { section: 'job' });
    this.dialog.open(AddResumeDialogComponent, {
      panelClass: 'add-resume-dialog',
      width: '95%',
      maxWidth: '1632px',
      maxHeight: '95vh',
      data: { mode: 'new', section: 'job' },
    }).afterClosed().subscribe((result?: AddResumeDialogResult) => {
      if (result?.resumeUploaded) {
        this.resumeUploaded = true;
        this.selectedJobId = '';
        this.getUserJobs();
      }
    });
  }
  
  filterJobList(event: JobFilterFormDataSchema) {
    this.getFilteredJobs(event);
  }

  getFilteredJobs(event: JobFilterFormDataSchema) {
    this.store.dispatch(updateLoaderState({ isLoading: true }));
    const roleType = event.role.join(',');
    const location = event.location.join(',');
    const education = event.education;
    const is_bookmarked = event.is_bookmarked;
    const is_related_to_career_goal = event.is_related_to_career_goal;

    this.jobApiService.getFilteredJobListing(
      this.sortBy,
      roleType, 
      location, 
      education,
      is_bookmarked, 
      is_related_to_career_goal
    ).pipe(
      finalize(() => this.store.dispatch(updateLoaderState({ isLoading: false })))
    ).subscribe({
      next: (data) => {
        this.saveUserJobs(data);
        this.store.dispatch(updateUserJobFilterOptions({ filter: event }));
      },
      error: (error) => {
        console.error('Error getting filtered jobs:', error);
        this.showErrorMessage('Error while applying filters. Please try again!');
      }
    });
  }

  showErrorMessage(msg: string) {
    this._snackBar.openFromComponent(WarningSnackBarComponent, {
      ...WarningDefaultOptions,
      data: { 
        message: msg,
      },
      duration: 5000,
    });
  }

  getSortedJobList(option: string) {
    this.store.dispatch(updateLoaderState({ isLoading: true }));
    const roleType = this.userFilterOptions.role.join(',');
    const location = this.userFilterOptions.location.join(',');
    const education = this.userFilterOptions.education;
    const is_bookmarked = this.userFilterOptions.is_bookmarked;
    const is_related_to_career_goal = this.userFilterOptions.is_related_to_career_goal;

    this.jobApiService.getSortedJobListing(
      option,
      roleType,
      location,
      education,
      is_bookmarked,
      is_related_to_career_goal
    ).pipe(
      finalize(() => this.store.dispatch(updateLoaderState({ isLoading: false })))
    ).subscribe({
      next: (data) => {
        this.saveUserJobs(data);
        this.store.dispatch(updateJobSortByOption({ sortBy: option }));
      },
      error: (error) => {
        console.error('Error fetching sorted jobs:', error);
        this.showErrorMessage('Error while sorting jobs. Please try again!');
      }
    });
  }

  getJobLocation(city: string, state: string): string {
    if(city && state) {
      return `${city}, ${state}`;
    } else if (city) {
      return city;
    } else if (state) {
      return state;
    } else {
      return 'N/A';
    }
  }

  getSalaryData(salary: any) {
    if (!salary) {
      return null;
    }
    return {
      ...salary,
      min: salary?.min ? this.getSalaryWithCommas(salary.min) : null,
      max: salary?.max ? this.getSalaryWithCommas(salary.max) : null,
    }
  }

  getSalaryWithCommas(salary: number): string {
    return salary.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  checkIfUserAppliedFilters() {
    let count = 0;
    const sortedSelectedType = [...this.userFilterOptions.role].sort();
    const sortedDefaultType = [...JOB_FILTER_DEFAULT_OPTIONS.role].sort();
    if (JSON.stringify(sortedSelectedType) !== JSON.stringify(sortedDefaultType)) count++;
    if (JSON.stringify(this.userFilterOptions.location) !== JSON.stringify(JOB_FILTER_DEFAULT_OPTIONS.location)) count++;
    if (this.userFilterOptions.education !== JOB_FILTER_DEFAULT_OPTIONS.education) count++;
    if (this.userFilterOptions.is_bookmarked !== JOB_FILTER_DEFAULT_OPTIONS.is_bookmarked) count++;
    if (this.isCareerGoalSet && !this.userFilterOptions.is_related_to_career_goal) count++;
    return count > 0;
  }

  openJobLandingDialog(): void {
    const dialogRef = this.dialog.open(JobLandingDialogComponent, {
      panelClass: 'job-landing-dialog',
      width: '95%',
      maxWidth: '800px',
      maxHeight: '95vh',
      data: { entrypoint: 'find_job_banner', section: 'job' },
    });

    dialogRef.afterClosed().subscribe((result?: JobLandingDialogResult) => {
      if (result) {
        this.jobLandingBanner?.hide();

        const sourceMap: Record<string, string> = {
          'Compass': 'compass',
          'Handshake': 'handshake',
          'LinkedIn': 'linked_in',
          'Networking or referral': 'networking_or_referral',
          'Other': 'other',
        };

        const payload: {
          work_type: string;
          employer_name: string;
          work_referrer?: string;
          work_referrer_other?: string;
        } = {
          work_type: result.roleType,
          employer_name: result.organization,
        };

        if (result.source) {
          const mappedSource = sourceMap[result.source];
          if (mappedSource && mappedSource !== 'other') {
            payload.work_referrer = mappedSource;
          } else {
            payload.work_referrer = 'other';
            payload.work_referrer_other = result.source;
          }
        }

        this.reportedWorkExperienceService.createReportedWorkExperience(payload)
          .pipe(take(1))
          .subscribe({
            error: (err) => console.error('Failed to save reported work experience', err),
          });
      }
    });
  }
}
